<script lang="ts">
  import { Info, CircleEllipsis, Trash2 } from "lucide-svelte"
  import { toast, xhr, wait, formatDate } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import InlineInput from "$app/components/InlineInput.svelte"
  import EditGoalStatus from "$app/Pages/Plans/Goals/EditGoalStatus.svelte"
  import Dropdown from "$app/components/Dropdown.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    task: TaskTypeE
    goal: GoalType
    pos?: number
    allowSelect?: boolean
    onopenTaskModal: (task: TaskType) => void
    onselected?: (task: TaskTypeE) => void
  }

  let {
    task = $bindable(),
    goal,
    pos = 0,
    allowSelect = true,
    onopenTaskModal,
    onselected = () => {}
  }: Props = $props()

  const plan = appStore.plan
  const channel_id = appStore.channel_id
  const departments = appStore.departments
  const users: UserType[] = appStore.users

  let openTaskDropdown = $state(false)

  function openTaskDetail() {
    onopenTaskModal(task)
  }

  async function updateAssignee(el: HTMLSelectElement | null): Promise<void> {
    if (!el) return
    console.log("Update assignee", el.value)
    const [a_type, a_id] = el.value.split("-")

    try {
      xhr.put(`/plans/${plan.id}/tasks/${task.id}.json`, {
        channel: channel_id,
        task: { assignee_type: a_type, assignee_id: a_id }
      })
    } catch (e: any) {
      toast("There was an error updating the assignee", "error")
    }
  }

  async function deleteTask(): Promise<void> {
    openTaskDropdown = false
    task._selected = true
    await wait(50)
    if (!confirm("Are you sure you want to delete this task?")) {
      task._selected = false
      return
    }

    try {
      const url = `/plans/${task.plan_id}/tasks/${task.id}.json?channel=${channel_id}`

      await xhr.delete(url)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error deleting the task", "error")
    }
  }

  function markTask(e: MouseEvent): void {
    const idx = appStore.tasks.findIndex((t) => t.id === task.id)
    const target = e.target as HTMLInputElement
    appStore.tasks[idx]._selected = target.checked

    if (target.checked) onselected(task)
  }

  function updateTask(data: any): void {
    const idx = appStore.tasks.findIndex((g) => g.id === task.id)
    appStore.tasks[idx] = { ...task, ...data }
  }

  function getAssignee(): DepartmentType | UserType | undefined {
    const arr = task.assignee_type === "Department" ? departments : users

    return arr.find((a) => a.id === task.assignee_id)
  }

  let selectedAssignee: DepartmentType | UserType | undefined = getAssignee()
</script>

<div
  class="border-l-4 border-blue-800 shadow-sm bg-white relative task"
  class:bg-blue-50={task._selected}
  data-id={task.id}
  id="task-{task.id}"
  ondblclick={openTaskDetail}
  role="button"
  tabindex="0"
>
  <!--Select checkbox-->
  {#if allowSelect}
    <div class="absolute top-1 left-1">
      <input
        type="checkbox"
        bind:checked={task._selected}
        onclick={(e) => {
          e.stopPropagation()
          markTask(e)
        }}
      />
    </div>
  {/if}

  <div
    class="pl-4 pr-2 py-2 border-gray-300 border-b border-t grid grid-cols-1"
  >
    <div class="grid grid-cols-12 ml-3 items-center gap-1">
      <div class="col-span-10 text-left flex items-center">
        <span class="text-gray-500 text-xs mr-1"
          >{pos > 0 ? `${pos}.` : ""}</span
        >
        <div class="w-full">
          <InlineInput
            bind:value={task.name}
            postURL={`/plans/${goal.plan_id}/tasks/${task.id}.json?channel=${channel_id}`}
            name="name"
            model="task"
          />
        </div>
      </div>

      <div class="col-span-2 flex items-center justify-center">
        <div class="relative inline-block text-left">
          <Dropdown bind:open={openTaskDropdown}>
            {#snippet button()}
              <span
                class="rounded-full flex items-center justify-center focus:outline-none"
              >
                <CircleEllipsis class="text-gray-400 w-7 h-7" />
              </span>
            {/snippet}

            <div>
              <div
                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none z-10"
                role="menu"
              >
                <div class="py-1" role="none">
                  <button
                    onclick={openTaskDetail}
                    class="btn-dropdown"
                    role="menuitem"
                  >
                    <Info class="text-gray-400 w-4 h-4 mr-3" />
                    See Details
                  </button>
                  <button
                    class="btn-dropdown"
                    role="menuitem"
                    onclick={deleteTask}
                  >
                    <Trash2 class="text-gray-400 w-4 h-4 mr-3" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-4 ml-3 pt-2 items-center gap-1 mr-3">
      <div class="flex items-center justify-start">
        <EditGoalStatus
          goalOrTask={task}
          type="Task"
          small={true}
          editable={true}
          show_border={false}
          onupdate={(data) => updateTask(data)}
        />
      </div>
      <div class="col-span-2 flex items-center justify-center">
        <p class="text-sm text-gray-800 flex items-center">
          <select
            onchange={(e) => updateAssignee(e.target as HTMLSelectElement)}
          >
            <optgroup label="Department">
              {#each departments as dept}
                <option
                  value="Department-{dept.id}"
                  selected={dept.id === task.assignee_id &&
                    task.assignee_type === "Department"}
                >
                  {dept.name}
                </option>
              {/each}
            </optgroup>
            <optgroup label="Member">
              {#each users as user}
                <option
                  value="User-{user.id}"
                  selected={user.id === task.assignee_id &&
                    task.assignee_type === "User"}
                >
                  {user.name}</option
                >
              {/each}
            </optgroup>
          </select>
        </p>
      </div>
      <div class="items-center justify-center flex">
        <p class="text-sm text-gray-800">
          {formatDate(task.expected_at)}
        </p>
      </div>

      <div class="w-3/12"></div>
    </div>
  </div>
</div>
